import './DateTime24Input.scss';

import React, { useEffect, useRef, useState } from 'react';
import { useClickAway } from 'react-use';

import bem from '../../utils/bem';
import type { DateTimeValues } from '../DateInput/utils';
import Segment from './Segment';
import {
  daysInMonth,
  isNumeric,
  MAX_DAYS_VALUE,
  MAX_HOURS_VALUE,
  MAX_MINUTES_VALUE,
  MAX_MONTH_VALUE,
  MAX_YEARS_VALUE,
  MIN_DATE_VALUE,
  MIN_TIME_VALUE,
  padTo,
  restrictNumberToTimeValue,
} from './utils';

export type DateTime24InputProps = {
  date: { month?: number; day?: number; year?: number; hour: number; minute: number };
  onChange: (next: string) => void;
  disabled?: boolean;
  readOnly?: boolean;
  invisible?: boolean;
  type?: 'date' | 'datetime-local';
  predefinedValues?: DateTimeValues;
};

type SegmentTypes = 'month' | 'day' | 'year' | 'hour' | 'minute' | null;

const SEGMENTS: SegmentTypes[] = ['month', 'day', 'year', 'hour', 'minute'];

const [block, element] = bem('datetime-24-input');

const DateTime24Input = ({
  date,
  onChange,
  disabled,
  readOnly,
  invisible,
  type,
  predefinedValues,
}: DateTime24InputProps): JSX.Element => {
  const modifiers = [readOnly ? 'readonly' : null, disabled ? 'disabled' : null, invisible ? 'invisible' : null];

  const timeCointainerRef = React.useRef<HTMLInputElement>(null);

  const [day, setDay] = useState<number | undefined>(date.day);
  const [month, setMonth] = useState<number | undefined>(date.month);
  const [year, setYear] = useState<number | undefined>(date.year);
  const [hour, setHour] = useState<number>(date.hour);
  const [minute, setMinute] = useState<number>(date.minute);

  const [active, setActive] = useState<SegmentTypes>(null);

  // typing buffer per segment (so '1' → '15')
  const bufferRef = useRef<{
    seg: SegmentTypes;
    buf: string;
    ts: number;
  } | null>(null);

  const Commit = (next: { day?: number; month?: number; year?: number; hour?: number; minute?: number }): void => {
    const yRaw = next.year ?? year;
    const mRaw = next.month ?? month;
    const dRaw = next.day ?? day;

    if (isNumeric(yRaw) && isNumeric(mRaw) && isNumeric(dRaw)) {
      const y = restrictNumberToTimeValue(Number(yRaw), MIN_DATE_VALUE, MAX_YEARS_VALUE);
      const m = restrictNumberToTimeValue(Number(mRaw), MIN_DATE_VALUE, 12);
      const d = restrictNumberToTimeValue(Number(dRaw), MIN_DATE_VALUE, daysInMonth(m, y));

      const h = restrictNumberToTimeValue(next.hour ?? hour, MIN_TIME_VALUE, MAX_HOURS_VALUE);
      const mi = restrictNumberToTimeValue(next.minute ?? minute, MIN_TIME_VALUE, MAX_MINUTES_VALUE);

      onChange(`${padTo(y, 4)}-${padTo(m, 2)}-${padTo(d, 2)}T${padTo(h)}:${padTo(mi)}`);
    }
  };

  const MoveActive = (dir: -1 | 1): void => {
    if (!active) return;
    const SegmentsSize = type === 'date' ? SEGMENTS.length - 2 : SEGMENTS.length;
    const i = SEGMENTS.indexOf(active);
    let nextIndex = i + dir;
    if (nextIndex > SegmentsSize - 1) nextIndex = 0;
    else if (nextIndex < 0) nextIndex = SegmentsSize - 1;
    setActive(SEGMENTS[nextIndex]);
  };

  const PrepareNewValue = (
    newValue: number,
    current: number,
    min: number,
    max: number,
    delta?: number,
    predefinedValue?: number
  ): number => {
    if (predefinedValue !== undefined) return predefinedValue;
    if (delta) {
      newValue = current + delta;
    }
    newValue = restrictNumberToTimeValue(newValue, min, max);
    return newValue;
  };

  const ChangeValue = (newValue: number, delta?: number): void => {
    if (!active) return;

    switch (active) {
      case 'day':
        newValue = PrepareNewValue(newValue, day ?? 0, MIN_DATE_VALUE, MAX_DAYS_VALUE, delta, predefinedValues?.day);
        setDay(newValue);
        Commit({ day: newValue });
        break;
      case 'month':
        newValue = PrepareNewValue(
          newValue,
          month ?? 0,
          MIN_DATE_VALUE,
          MAX_MONTH_VALUE,
          delta,
          predefinedValues?.month
        );
        setMonth(newValue);
        Commit({ month: newValue });
        break;
      case 'year':
        newValue = PrepareNewValue(newValue, year ?? 0, MIN_DATE_VALUE, MAX_YEARS_VALUE, delta, predefinedValues?.year);
        setYear(newValue);
        Commit({ year: newValue });
        break;
      case 'hour':
        newValue = PrepareNewValue(newValue, hour, MIN_TIME_VALUE, MAX_HOURS_VALUE, delta, predefinedValues?.hours);
        setHour(newValue);
        Commit({ hour: newValue });
        break;
      case 'minute':
        newValue = PrepareNewValue(
          newValue,
          minute,
          MIN_TIME_VALUE,
          MAX_MINUTES_VALUE,
          delta,
          predefinedValues?.minutes
        );
        setMinute(newValue);
        Commit({ minute: newValue });
        break;
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (disabled) return;

    // navigation between segments
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      MoveActive(-1);
      return;
    }
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      MoveActive(1);
      return;
    }

    // increment / decrement
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      ChangeValue(0, 1);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      ChangeValue(0, -1);
      return;
    }

    // numeric typing
    if (/^\d$/.test(e.key)) {
      e.preventDefault();
      const now = Date.now();
      const prev = bufferRef.current;
      const fresh = !prev || prev.seg !== active || now - prev.ts > 900;

      const maxLen = active === 'year' ? 4 : 2;
      const buf = (fresh ? '' : prev?.buf) + e.key;
      const nextBuf = buf.slice(-maxLen);
      bufferRef.current = { seg: active, buf: nextBuf, ts: now };

      const num = Number(nextBuf);

      if (active !== null) ChangeValue(num);
    }
  };

  const handleActiveChange = (segment: SegmentTypes): void => {
    if (disabled) return;
    if (timeCointainerRef.current) {
      timeCointainerRef.current.focus();
    }
    setActive(segment);
  };

  useClickAway(timeCointainerRef, (e: MouseEvent) => {
    e.stopPropagation();
    setActive(null);
  });

  useEffect(() => {
    setMonth(date.month);
    setDay(date.day);
    setYear(date.year);
    setHour(date.hour);
    setMinute(date.minute);
  }, [date.day, date.hour, date.minute, date.month, date.year]);

  return (
    <div className={`${block(modifiers)}`} style={{ display: 'inline-flex' }} data-testid="datetime-input-container">
      <div
        ref={timeCointainerRef}
        tabIndex={disabled ? -1 : 0}
        role="group"
        aria-label="date time picker"
        onKeyDown={onKeyDown}
        className={element('time-input')}
        id="time-input"
        data-testid="date-time-input"
      >
        <Segment
          value={isNumeric(month) ? padTo(Number(month), 2) : 'mm'}
          active={active === 'month'}
          onClick={(): void => handleActiveChange('month')}
        />
        <span>/</span>
        <Segment
          value={isNumeric(day) ? padTo(Number(day), 2) : 'dd'}
          active={active === 'day'}
          onClick={(): void => handleActiveChange('day')}
        />
        <span>/</span>
        <Segment
          value={isNumeric(year) ? padTo(Number(year), 4) : 'yyyy'}
          active={active === 'year'}
          onClick={(): void => handleActiveChange('year')}
        />
        {type === 'datetime-local' && (
          <>
            <span style={{ padding: '0 4px' }} />
            <Segment
              value={padTo(hour, 2)}
              active={active === 'hour'}
              onClick={(): void => handleActiveChange('hour')}
            />
            <span style={{ padding: '0 2px' }}>:</span>
            <Segment
              value={padTo(minute, 2)}
              active={active === 'minute'}
              onClick={(): void => handleActiveChange('minute')}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default DateTime24Input;
